import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { GrowthCharts } from "../components/GrowthCharts";
import { SpecPhaseDonut } from "../components/SpecPhaseDonut";
import { FunnelStrip, type FunnelStage } from "../components/FunnelStrip";
import { Th } from "../components/Table";
import { InfoHint } from "../components/Tooltip";
import { EntityTagSection } from "../components/EntityTagSection";
import { ActivityTable } from "../components/ActivityTable";
import { useClientSort } from "../hooks/useClientSort";
import { relativeTime } from "../lib/activity";
import type { UserTag, OrgActivity } from "../api/types";

// Org detail (spec-3): the activation funnel strip + spec-phase distribution +
// memex/user counts, a manual "internal" claim (dec-4), and an Open-in-Mixpanel
// link for the aggregate behavioural cut (dec-3 — link, not embed).
interface OrgDetail {
  id: string;
  name: string;
  handle: string | null;
  createdAt: string;
  updatedAt: string;
  domainVerified: boolean;
  memexCount: number;
  userCount: number;
  isInternal: boolean;
  tags: UserTag[];
}
interface Funnel {
  signedUp: boolean;
  firstSpec: boolean;
  decisionResolved: boolean;
  acDefined: boolean;
  mcpConnected: boolean;
  mcpToolFired: boolean;
  testDataReceived: boolean;
  specInVerify: boolean;
  specInDone: boolean;
  dates: Record<string, string | null>;
}
type Phases = Record<"draft" | "specify" | "build" | "verify" | "done", number>;
interface OrgMemex {
  id: string;
  name: string;
  handle: string;
  createdAt: string;
  specCount: number;
}
interface OrgUser {
  id: string;
  name: string | null;
  email: string;
  firstSeenAt: string;
  mcpSessions: number;
  specsCreated: number;
  activeDays: number;
  lastActivityAt: string | null;
}

// Whole-days since an ISO timestamp; null activity → null (rendered as "—").
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

const FUNNEL_STAGES: FunnelStage[] = [
  { key: "signedUp", label: "Signed up", hint: "At least one person in the org has a Memex account." },
  { key: "firstSpec", label: "First spec", hint: "The org has created at least one spec." },
  { key: "decisionResolved", label: "Decision resolved", hint: "At least one decision has been resolved in the org's memexes." },
  { key: "acDefined", label: "AC defined", hint: "At least one acceptance criterion has been defined." },
  { key: "mcpConnected", label: "MCP connected", hint: "Someone has connected an MCP client (e.g. Claude/Cursor) to a memex." },
  { key: "mcpToolFired", label: "MCP tool fired", hint: "An MCP tool call has actually fired — the agent is in use." },
  { key: "testDataReceived", label: "AC test data received", hint: "An acceptance criterion has received real test telemetry (a test run reported pass/fail)." },
  { key: "specInVerify", label: "First spec in Verify", hint: "A spec has reached the verify phase." },
  { key: "specInDone", label: "First spec in Done", hint: "A spec has reached done." },
];
const PHASE_ORDER: (keyof Phases)[] = ["draft", "specify", "build", "verify", "done"];
// The memex-prod Mixpanel project (id 4033258) — the behavioural cut lives here.
const MIXPANEL_PROJECT_URL = "https://mixpanel.com/project/4033258";

// Sort accessors for the client-side-sortable org tables (stable module consts).
const USER_SORTS: Record<string, (u: OrgUser) => string | number | null> = {
  user: (u) => (u.name ?? u.email).toLowerCase(),
  mcpSessions: (u) => u.mcpSessions,
  specsCreated: (u) => u.specsCreated,
  activeDays: (u) => u.activeDays,
  firstSeenAt: (u) => Date.parse(u.firstSeenAt),
  lastActivityAt: (u) => (u.lastActivityAt ? Date.parse(u.lastActivityAt) : null),
};
const MEMEX_SORTS: Record<string, (m: OrgMemex) => string | number | null> = {
  name: (m) => m.name.toLowerCase(),
  specCount: (m) => m.specCount,
  createdAt: (m) => Date.parse(m.createdAt),
};

export function OrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [phases, setPhases] = useState<Phases | null>(null);
  const [memexes, setMemexes] = useState<OrgMemex[]>([]);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [activity, setActivity] = useState<OrgActivity | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "notfound" | "error">("loading");

  const userSort = useClientSort(users, USER_SORTS, "lastActivityAt", "desc");
  const memexSort = useClientSort(memexes, MEMEX_SORTS, "createdAt", "desc");

  useEffect(() => {
    if (!id) return;
    api<{ org: OrgDetail }>(`/api/orgs/${id}`)
      .then((r) => {
        setOrg(r.org);
        setStatus("ok");
      })
      .catch((e) => setStatus(e?.status === 404 ? "notfound" : "error"));
    api<{ funnel: Funnel }>(`/api/orgs/${id}/funnel`).then((r) => setFunnel(r.funnel)).catch(() => {});
    api<{ phases: Phases }>(`/api/orgs/${id}/phases`).then((r) => setPhases(r.phases)).catch(() => {});
    api<{ memexes: OrgMemex[] }>(`/api/orgs/${id}/memexes`).then((r) => setMemexes(r.memexes ?? [])).catch(() => {});
    api<{ users: OrgUser[] }>(`/api/orgs/${id}/users`).then((r) => setUsers(r.users ?? [])).catch(() => {});
    api<{ activity: OrgActivity }>(`/api/orgs/${id}/activity`).then((r) => setActivity(r.activity)).catch(() => {});
  }, [id]);

  async function toggleInternal() {
    if (!id || !org) return;
    const next = !org.isInternal;
    await api(`/api/orgs/${id}/classification`, {
      method: "PUT",
      body: JSON.stringify({ isInternal: next }),
    });
    setOrg({ ...org, isInternal: next });
  }

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <Link to="/" className="text-sm text-muted hover:text-primary">
        &larr; Overview
      </Link>

      {status === "loading" && <p className="mt-6 text-muted">Loading…</p>}
      {status === "notfound" && <p className="mt-6 text-status-danger-text">Org not found.</p>}
      {status === "error" && <p className="mt-6 text-status-danger-text">Failed to load org.</p>}

      {status === "ok" && org && (
        <>
          <header className="mt-4 mb-6 flex items-start justify-between">
            <div>
              <h1 className="text-2xl text-heading">{org.name}</h1>
              <p className="text-sm text-muted">{org.handle ? `@${org.handle}` : "no handle"}</p>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={MIXPANEL_PROJECT_URL}
                target="_blank"
                rel="noreferrer"
                title="Open the memex-prod Mixpanel project for the aggregate behavioural analytics. Backstage is the live/named view; Mixpanel is the aggregate one."
                className="text-sm text-accent hover:underline"
                data-testid="open-in-mixpanel"
              >
                Open in Mixpanel ↗
              </a>
              <button
                onClick={toggleInternal}
                data-testid="claim-internal"
                title="Mark this org as internal (Mindset's own) so the 'Hide internal' filter can drop it from the board. Stored only in Backstage, never in the product DB."
                className="rounded-lg border border-edge px-3 py-1.5 text-sm text-secondary hover:bg-card-hover"
              >
                {org.isInternal ? "Unmark internal" : "Mark internal"}
              </button>
            </div>
          </header>

          <section className="mb-6" data-testid="org-tags">
            <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted">Tags</h2>
            <EntityTagSection entityType="org" entityId={org.id} initialTags={org.tags} />
          </section>

          <div className="mb-6">
            <GrowthCharts query={`org=${id}`} variant="org" />
          </div>

          {/* Left column: donut + phase card-lets + half-size stat cards stacked.
              Right column: the vertical activation funnel beside the donut. */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <section data-testid="phase-distribution">
              <h2 className="mb-2 flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted">
                Specs by phase
                <InfoHint text="The CURRENT distribution of the org's specs across SDD phases (draft → specify → build → verify → done). A snapshot, not over time." />
              </h2>
              <SpecPhaseDonut phases={phases ?? { draft: 0, specify: 0, build: 0, verify: 0, done: 0 }} />
              <div className="mt-3 grid grid-cols-5 gap-2">
                {PHASE_ORDER.map((p) => (
                  <div key={p} className="rounded-lg border border-edge bg-surface px-2 py-2 text-center">
                    <div className="text-lg font-semibold text-heading" data-testid={`phase-${p}`}>
                      {phases?.[p] ?? 0}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted">{p}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Stat label="memexes" value={org.memexCount} small />
                <Stat label="users" value={org.userCount} small />
              </div>
            </section>

            <FunnelStrip
              stages={FUNNEL_STAGES}
              funnel={(funnel ?? {}) as unknown as Record<string, boolean>}
              dates={funnel?.dates}
              hint="The org-level activation funnel. Each stage lights green once at least one person/entity in the org has reached it; the date is when it was first reached."
            />
          </div>

          {activity && (
            <section className="mt-6" data-testid="org-activity">
              <h2 className="mb-2 flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted">
                Most active members
                <InfoHint text="The org's members ranked by recent engagement (busyness over the last 7 days). The aggregate is the sum of members' busyness — it matches each member row's org busyness." />
              </h2>
              <div className="mb-3 grid grid-cols-3 gap-3">
                <Stat label="org busyness" value={activity.aggregate.busyness.toFixed(1)} small />
                <Stat label="org volume (7d)" value={activity.aggregate.volume} small />
                <div className="rounded-xl border border-edge bg-surface px-4 py-2.5" data-testid="org-aggregate-last-action">
                  <div className="text-sm font-semibold text-heading">
                    {activity.aggregate.lastAction
                      ? relativeTime(activity.aggregate.lastAction.at)
                      : "—"}
                  </div>
                  <div className="mt-0.5 text-xs uppercase tracking-wider text-muted">last action</div>
                </div>
              </div>
              {activity.members.length === 0 ? (
                <p className="rounded-xl border border-edge px-4 py-3 text-sm text-muted">No recent activity.</p>
              ) : (
                <ActivityTable rows={activity.members} />
              )}
            </section>
          )}

          <section className="mt-6" data-testid="org-users">
            <h2 className="mb-2 flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted">
              Users ({users.length})
              <InfoHint text="The org's active members and their lifetime vitals. Click any column header to sort." />
            </h2>
            <div className="overflow-hidden rounded-xl border border-edge">
              {users.length === 0 ? (
                <p className="px-4 py-3 text-sm text-muted">No users yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-edge">
                      <Th label="User" field="user" sort={userSort.sort} order={userSort.order} onSort={userSort.toggleSort} hint="Name / email. Click to open the user's detail." />
                      <Th label="MCP sessions" field="mcpSessions" sort={userSort.sort} order={userSort.order} onSort={userSort.toggleSort} align="right" hint="Total MCP sessions this user has ever started (lifetime)." />
                      <Th label="Specs" field="specsCreated" sort={userSort.sort} order={userSort.order} onSort={userSort.toggleSort} align="right" hint="Specs this user has created (non-archived)." />
                      <Th label="Active days" field="activeDays" sort={userSort.sort} order={userSort.order} onSort={userSort.toggleSort} align="right" hint="Distinct calendar days the user was active (any tool call, session, or doc created)." />
                      <Th label="First seen" field="firstSeenAt" sort={userSort.sort} order={userSort.order} onSort={userSort.toggleSort} align="right" hint="When the user first signed up." />
                      <Th label="Last active" field="lastActivityAt" sort={userSort.sort} order={userSort.order} onSort={userSort.toggleSort} align="right" hint="Days since their most recent activity — latest tool call, session, or doc created." />
                    </tr>
                  </thead>
                  <tbody>
                    {userSort.sorted.map((u) => {
                      const days = daysSince(u.lastActivityAt);
                      return (
                        <tr key={u.id} data-testid="org-user-row" className="border-b border-edge-subtle last:border-0 hover:bg-card-hover">
                          <td className="px-4 py-2.5">
                            <Link to={`/users/${u.id}`} className="font-medium text-primary hover:text-accent">
                              {u.name ?? u.email}
                            </Link>
                            <div className="text-xs text-muted">{u.email}</div>
                          </td>
                          <td className="px-4 py-2.5 text-right text-secondary">{u.mcpSessions}</td>
                          <td className="px-4 py-2.5 text-right text-secondary">{u.specsCreated}</td>
                          <td className="px-4 py-2.5 text-right text-secondary">{u.activeDays}</td>
                          <td className="px-4 py-2.5 text-right text-muted">
                            {new Date(u.firstSeenAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2.5 text-right text-muted">
                            {days === null ? "—" : days === 0 ? "today" : `${days}d ago`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="mt-6" data-testid="org-memexes">
            <h2 className="mb-2 flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted">
              Memexes ({memexes.length})
              <InfoHint text="The memexes (workspaces) in this org's namespace. Click any column header to sort; click a row to open the memex." />
            </h2>
            <div className="overflow-hidden rounded-xl border border-edge">
              {memexes.length === 0 ? (
                <p className="px-4 py-3 text-sm text-muted">No memexes yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-edge">
                      <Th label="Memex" field="name" sort={memexSort.sort} order={memexSort.order} onSort={memexSort.toggleSort} hint="Memex name. Click the row to open its detail." />
                      <Th label="Specs" field="specCount" sort={memexSort.sort} order={memexSort.order} onSort={memexSort.toggleSort} align="right" hint="Specs in this memex (non-archived)." />
                      <Th label="Created" field="createdAt" sort={memexSort.sort} order={memexSort.order} onSort={memexSort.toggleSort} align="right" hint="When the memex was created." />
                    </tr>
                  </thead>
                  <tbody>
                    {memexSort.sorted.map((m) => (
                      <tr key={m.id} className="border-b border-edge-subtle last:border-0 hover:bg-card-hover">
                        <td className="px-4 py-2.5">
                          <Link to={`/memexes/${m.id}`} data-testid="org-memex-row" className="font-medium text-primary hover:text-accent">
                            {m.name}
                          </Link>
                          <span className="ml-2 text-xs text-muted">@{m.handle}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-secondary">{m.specCount}</td>
                        <td className="px-4 py-2.5 text-right text-muted">{new Date(m.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <dl className="mt-6 divide-y divide-edge-subtle rounded-xl border border-edge">
            <Meta label="Created" value={new Date(org.createdAt).toLocaleString()} />
            <Meta label="Last updated" value={new Date(org.updatedAt).toLocaleString()} />
            <div className="flex justify-between px-5 py-3 text-sm">
              <dt className="text-muted">Domain verified</dt>
              <dd>
                <span
                  className={
                    org.domainVerified
                      ? "rounded-full border border-status-success-border bg-status-success-bg px-2 py-0.5 text-xs text-status-success-text"
                      : "rounded-full border border-status-neutral-border bg-status-neutral-bg px-2 py-0.5 text-xs text-status-neutral-text"
                  }
                >
                  {org.domainVerified ? "verified" : "unverified"}
                </span>
              </dd>
            </div>
          </dl>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, small = false }: { label: string; value: string | number; small?: boolean }) {
  return (
    <div className={`rounded-xl border border-edge bg-surface ${small ? "px-4 py-2.5" : "px-5 py-4"}`}>
      <div className={`font-semibold text-heading ${small ? "text-2xl" : "text-3xl"}`} data-testid={`stat-${label}`}>
        {value}
      </div>
      <div className="mt-0.5 text-xs uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between px-5 py-3 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="text-primary">{value}</dd>
    </div>
  );
}
